import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import { CalculatorUsFieldItemsService } from "../../../../../services/calculator-us-field-items.service";
import { DxAccordionComponent, DxFormComponent } from "devextreme-angular";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { BinderySpecification } from "../../../../../model/calculator-us-remastered/binderySpecification";
import { window } from "rxjs/operators";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";

@Component({
  selector: "app-calculator-bindery",
  templateUrl: "./calculator-bindery.component.html",
  styleUrls: ["./calculator-bindery.component.scss"],
})
export class CalculatorBinderyComponent implements OnChanges {
  @Input() selectedClassification: string | undefined;
  @Input() selectedPublisherId: string | undefined;
  @Output() summaryUpdate = new EventEmitter<SummaryItem[]>();
  @Output() onValueChanged = new EventEmitter<BinderySpecification>();

  @ViewChild("binderyForm") binderyForm: DxFormComponent;
  @ViewChild("accordion") accordion: DxAccordionComponent;

  private _binderySpecification: BinderySpecification;
  @Input()
  set binderySpecification(val: BinderySpecification) {
    this._binderySpecification = val;
  }
  get binderySpecification(): BinderySpecification {
    return this._binderySpecification;
  }

  formatMessage = formatMessage;
  collapsed: any = true;

  // Selected values
  private _selectedHeadband: UsCalculatorFieldItem | null = null;
  get selectedHeadband(): UsCalculatorFieldItem | null {
    return this._selectedHeadband;
  }

  set selectedHeadband(value: UsCalculatorFieldItem | null) {
    this._selectedHeadband = value;
    this.binderySpecification.headband = value?.keyValue;

    this.updateSummary();
    this.onValueChanged.emit(this.binderySpecification);
  }

  private _selectedBindStyle: UsCalculatorFieldItem | null = null;
  get selectedBindStyle(): UsCalculatorFieldItem | null {
    return this._selectedBindStyle;
  }

  set selectedBindStyle(value: UsCalculatorFieldItem | null) {
    this._selectedBindStyle = value;
    this.binderySpecification.bindStyle = value?.keyValue;

    this.updateSummary();
    this.onValueChanged.emit(this.binderySpecification);
  }

  private _cartonsPackOptionId: string | undefined;
  get cartonsPackOptionId() {
    return this._cartonsPackOptionId;
  }
  set cartonsPackOptionId(value: string | undefined) {
    this._cartonsPackOptionId = value;
    this.binderySpecification.cartonsPackOptionId = value;
    this.onValueChanged.emit(this._binderySpecification);
  }
  private _selectedCartonsPackValue: UsCalculatorFieldItem | undefined;
  get selectedCartonsPackValue() {
    return this._selectedCartonsPackValue;
  }
  set selectedCartonsPackValue(value: UsCalculatorFieldItem | undefined) {
    this._selectedCartonsPackValue = value;
    this.binderySpecification.cartonsPackValue =
      value?.keyValue !== undefined ? +value.keyValue : 0;

    this.updateSummary();
    this.onValueChanged.emit(this._binderySpecification);
  }

  packDataSource: UsCalculatorFieldItem[] = [];
  bindStyleDataSource: UsCalculatorFieldItem[] = [];
  headbandDataSource: UsCalculatorFieldItem[] = [];

  showHeadband: boolean = true;
  bindStyleColSpan: number = 3;

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedClassification) {
      // Reset selected values when publisher or classification changes
      this.resetFieldItems();
      this.loadFieldItems();
    }

    if (changes.selectedClassification) {
      this.showHeadband =
        this.selectedClassification?.includes("HARDCOVER") || false;
      this.bindStyleColSpan = this.showHeadband ? 3 : 6;
    }
  }

  private loadFieldItems(): void {
    if (!this.selectedPublisherId || !this.selectedClassification) {
      return;
    }

    // Verwende optimierte Methode für gesammelte Subkategorie-Abfrage
    this.calculatorUsFieldItemsService
      .getFieldItemsBySubcategory(
        this.selectedPublisherId,
        this.calculatorUsFieldItemsService.getDbClassification(
          this.selectedClassification
        ),
        "BINDERY"
      )
      .subscribe((fieldItems) => {
        // Reset arrays
        this.headbandDataSource = [];
        this.bindStyleDataSource = [];
        this.packDataSource = [];

        // Mapping über fieldItem.inputName wie in der Hauptkomponente
        let defaultPackValue: string | undefined;
        for (const fieldItem of fieldItems) {
          if (fieldItem.inputName === "HEADBAND") {
            this.headbandDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedHeadband = fieldItem;
            }
          } else if (fieldItem.inputName === "BIND_STYLE") {
            this.bindStyleDataSource.push(fieldItem);
            if (fieldItem.isDefault) {
              this._selectedBindStyle = fieldItem;
            }
          } else if (fieldItem.inputName === "PACK") {
            if (fieldItem.inputType === "OPTION_SELECT") {
              // it is the option id
              this.cartonsPackOptionId = fieldItem.keyValue;
              defaultPackValue = fieldItem.defaultValue;
            } else if (fieldItem.inputType === "OPTION_TEXT") {
              // it is one of the cartons options (columns)
              this.packDataSource.push(fieldItem);
            }
          }
        }
        // set the default PACK value based on the default value of the corresponding OPTION_SELECT
        this.selectedCartonsPackValue = fieldItems.find(
          (item) =>
            item.inputName === "PACK" && item.keyValue === defaultPackValue
        );

        this.updateSummary();
      });
  }

  public resetFieldItems(): void {
    this.selectedHeadband = null;
    this.selectedBindStyle = null;
    this.selectedCartonsPackValue = undefined;

    this.headbandDataSource = [];
    this.bindStyleDataSource = [];
    this.packDataSource = [];

    if (this.accordion) this.accordion.instance.collapseItem(0).then();
    this.summaryUpdate.emit([]);
  }

  onAccordionClick(e) {
    this.collapsed = e.component.option("selectedIndex") == -1;
  }

  // Event handlers for value changes
  headbandChanged(event: any): void {
    const selectedValue = event.value;
    this.selectedHeadband =
      this.headbandDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;
    this.updateSummary();
  }

  bindStyleChanged(event: any): void {
    const selectedValue = event.value;
    this.selectedBindStyle =
      this.bindStyleDataSource?.find(
        (item: any) => item.keyValue === selectedValue
      ) || null;
    this.updateSummary();
  }

  private updateSummary(): void {
    const summaryItems: SummaryItem[] = [];

    // Headband
    if (this._selectedHeadband) {
      const defaultHeadband = this.headbandDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "headband",
          "Headband",
          this._selectedHeadband.displayValue,
          defaultHeadband?.displayValue || "",
          "Bindery",
          undefined
        )
      );
    }

    // Bind Style
    if (this._selectedBindStyle) {
      const defaultBindStyle = this.bindStyleDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "bind_style",
          "Bind Style",
          this._selectedBindStyle.displayValue,
          defaultBindStyle?.displayValue || "",
          "Bindery",
          undefined
        )
      );
    }

    // Pack
    if (this._selectedCartonsPackValue) {
      const defaultPack = this.packDataSource?.find(
        (item: any) => item.isDefault
      );
      summaryItems.push(
        new SummaryItem(
          "pack",
          "Pack",
          this._selectedCartonsPackValue.displayValue,
          defaultPack?.displayValue || "",
          "Bindery",
          undefined
        )
      );
    }

    this.summaryUpdate.emit(summaryItems);
  }
}
